import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Briefcase,
  UserPlus,
  CalendarDays,
  ClipboardCheck,
  CheckCircle2,
  Star,
  Pencil,
  UserPlus2,
  Zap,
  Eye,
  FileText,
  Calendar,
  Search,
  MessageSquare,
  CheckCircle,
  Clock,
  Loader2,
  Trash2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { toast } from "sonner";
import { formatSydneyDateLong, formatSydneyDateShort } from "../../utils/helpers";
import { useAuth } from "../../context/AuthContext";

interface Requisition {
  id: number;
  urgency: string;
  startDate: string | null;
  businessJustification: string | null;
  salaryRangeMin: number | null;
  salaryRangeMax: number | null;
  keyDuties: string[];
  outcomes: string[];
  status: string;
  jobRole: { id: number; roleName: string; jobGrade?: { id: number; name: string; salaryRangeMin?: number | null; salaryRangeMax?: number | null } | null } | null;
  jobPositions: { id: number; positionCode: string; location: string | null }[];
  department: { id: number; departmentName: string } | null;
  reportsTo: { id: number; name: string } | null;
  createdBy: { id: number; name: string } | null;
  createdAt: string;
  requisitionViewers: { id: number; user: { id: number; name: string; email: string } }[];
}

interface Candidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  currentPosition: string | null;
  currentCompany: string | null;
  source: string | null;
  yearsExperience: number | null;
  linkedInProfile: string | null;
  status: string;
  createdAt: string;
}

const formatStatusLabel = (status: string) => {
  switch (status) {
    case "OPEN": return "Open";
    case "IN_PROGRESS": return "In Progress";
    case "ON_HOLD": return "On Hold";
    case "COMPLETE": return "Complete";
    case "CANCELLED": return "Cancelled";
    default: return status;
  }
};

const getStatusBadgeStyle = (status: string) => {
  switch (status) {
    case "OPEN":
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    case "IN_PROGRESS":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "ON_HOLD":
      return "bg-purple-50 text-purple-700 border-purple-100";
    case "COMPLETE":
      return "bg-green-50 text-green-700 border-green-100";
    case "CANCELLED":
      return "bg-red-50 text-red-700 border-red-100";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const getUrgencyBadgeStyle = (urgency: string) => {
  switch (urgency) {
    case "Critical":
      return "bg-red-50 text-red-700 border-red-100";
    case "High":
      return "bg-orange-50 text-orange-700 border-orange-100";
    case "Medium":
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    case "Low":
      return "bg-green-50 text-green-700 border-green-100";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const getCandidateStatusStyle = (status: string) => {
  switch (status) {
    case "SHORTLISTED":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "PHONE_SCREEN":
      return "bg-indigo-50 text-indigo-700 border-indigo-100";
    case "INTERVIEW":
      return "bg-slate-800 text-white border-slate-800";
    case "PRE_EMPLOYMENT_COMMENCED":
      return "bg-teal-50 text-teal-700 border-teal-100";
    case "PRE_EMPLOYMENT_COMPLETED":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "DRAFT_OFFER":
      return "bg-purple-50 text-purple-700 border-purple-100";
    case "OFFER_SENT":
      return "bg-purple-50 text-purple-700 border-purple-100";
    case "OFFER_ACCEPTED":
    case "HIRING_COMMENCED":
    case "HIRED":
      return "bg-green-50 text-green-700 border-green-100";
    case "REJECTED":
    case "OFFER_REJECTED":
    case "OFFER_EXPIRED":
    case "WITHDREW":
      return "bg-gray-100 text-gray-600 border-gray-100";
    default:
      return "bg-gray-100 text-gray-700 border-gray-100";
  }
};

const formatCandidateStatus = (status: string) => {
  switch (status) {
    case "SHORTLISTED": return "Shortlisted";
    case "PHONE_SCREEN": return "Phone Screen";
    case "INTERVIEW": return "Interview";
    case "PRE_EMPLOYMENT_COMMENCED": return "Pre-Employment";
    case "PRE_EMPLOYMENT_COMPLETED": return "Pre-Employment Done";
    case "DRAFT_OFFER": return "Draft Offer";
    case "OFFER_SENT": return "Offer Sent";
    case "OFFER_ACCEPTED": return "Offer Accepted";
    case "OFFER_REJECTED": return "Offer Rejected";
    case "OFFER_EXPIRED": return "Offer Expired";
    case "REJECTED": return "Rejected";
    case "WITHDREW": return "Withdrew";
    case "HIRING_COMMENCED": return "Hiring Commenced";
    case "HIRED": return "Hired";
    default: return status.replace(/_/g, " ");
  }
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatSydneyDateLong(dateStr) || "—";
};

const formatShortDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatSydneyDateShort(dateStr) || "—";
};

const formatSalary = (min: number | null, max: number | null) => {
  if (min && max) {
    return `$${min.toLocaleString()} — $${max.toLocaleString()}`;
  }
  if (min) {
    return `$${min.toLocaleString()}+`;
  }
  if (max) {
    return `Up to $${max.toLocaleString()}`;
  }
  return "Not specified";
};

const RequisitionDetail: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();

  const canReadAll = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_REQUEST_DETAIL_ALL);
  const canReadOwn = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_REQUEST_DETAIL_CREATED_BY_ME);

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [req, setReq] = useState<Requisition | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Viewers
  const [availableViewers, setAvailableViewers] = useState<{ id: number; name: string; email: string }[]>([]);
  const [removingViewerId, setRemovingViewerId] = useState<number | null>(null);
  const [viewerPopoverOpen, setViewerPopoverOpen] = useState(false);
  const canManageViewers = canReadAll || (canReadOwn && String(req?.createdBy?.id) === String(currentUser?.id));

  // Fetch requisition + candidates
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      api.get<{ success: boolean; requisition: Requisition }>(`/api/requisitions/${id}`),
      api.get<{ success: boolean; candidates: Candidate[] }>(`/api/requisitions/${id}/candidates`),
    ])
      .then(([reqRes, candRes]) => {
        if (reqRes.success) setReq(reqRes.requisition);
        if (candRes.success) setCandidates(candRes.candidates);
      })
      .catch((err) => {
        console.error("Failed to load requisition:", err);
        toast.error("Failed to load requisition details");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const fetchAvailableViewers = async () => {
    try {
      const res = await api.get<{ success: boolean; moduleAccess: { user: { id: number; name: string; email: string }; accessLevel: string }[] }>("/api/requisitions/module-access");
      if (res.success) {
        const viewers = res.moduleAccess
          .filter((m) => m.accessLevel === "VIEWER")
          .map((m) => m.user)
          .filter((u) => !currentUser?.id || String(u.id) !== currentUser.id);
        setAvailableViewers(viewers);
      }
    } catch (err) {
      console.error("Failed to load available viewers:", err);
    }
  };

  useEffect(() => {
    if (req && canManageViewers) {
      fetchAvailableViewers();
    }
  }, [req?.id ?? 0]);

  const handleRemoveViewer = async (userId: number) => {
    if (!req) return;
    setRemovingViewerId(userId);
    try {
      await api.delete(`/api/requisitions/${req.id}/viewers/${userId}`);
      setReq((prev) => {
        if (!prev) return prev;
        return { ...prev, requisitionViewers: prev.requisitionViewers.filter((v) => v.user.id !== userId) };
      });
      toast.success("Viewer removed");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove viewer");
    } finally {
      setRemovingViewerId(null);
    }
  };

  // Computed stats from candidates
  const totalCandidates = candidates.length;
  const activeInterviews = candidates.filter((c) => c.status === "INTERVIEW").length;
  const preEmploymentCommenced = candidates.filter((c) => c.status === "PRE_EMPLOYMENT_COMMENCED").length;
  const preEmploymentCompleted = candidates.filter((c) => c.status === "PRE_EMPLOYMENT_COMPLETED").length;
  const offersExtended = candidates.filter((c) => c.status === "OFFER_SENT").length;
  const successfulHires = candidates.filter((c) => c.status === "HIRING_COMMENCED" || c.status === "HIRED" || c.status === "OFFER_ACCEPTED").length;

  const stats = [
    { label: "Total Candidates", value: totalCandidates, icon: <UserPlus size={22} />, iconColor: "text-blue-500", iconBg: "bg-blue-50" },
    { label: "Active Interviews", value: activeInterviews, icon: <CalendarDays size={22} />, iconColor: "text-green-500", iconBg: "bg-green-50" },
    { label: "Pre Employment Commenced", value: preEmploymentCommenced, icon: <ClipboardCheck size={22} />, iconColor: "text-teal-500", iconBg: "bg-teal-50" },
    { label: "Pre Employment Completed", value: preEmploymentCompleted, icon: <ClipboardCheck size={22} />, iconColor: "text-emerald-500", iconBg: "bg-emerald-50" },
    { label: "Offers Extended", value: offersExtended, icon: <CheckCircle2 size={22} />, iconColor: "text-purple-500", iconBg: "bg-purple-50" },
    { label: "Successful Hires", value: successfulHires, icon: <Star size={22} />, iconColor: "text-amber-500", iconBg: "bg-amber-50" },
  ];

  const candidatesTarget = Math.max(req?.jobPositions?.length || 1, 3) * 3;
  const candidatesProgress = Math.min((totalCandidates / candidatesTarget) * 100, 100);
  const interviewProgress = Math.min((activeInterviews / candidatesTarget) * 100, 100);

  // Allowed status transitions
  const allowedNextStatuses = useMemo(() => {
    switch (req?.status) {
      case "OPEN": return ["IN_PROGRESS", "ON_HOLD", "COMPLETE", "CANCELLED"];
      case "IN_PROGRESS": return ["ON_HOLD", "COMPLETE", "CANCELLED"];
      case "ON_HOLD": return ["IN_PROGRESS", "CANCELLED"];
      default: return [];
    }
  }, [req?.status]);

  const handleStatusChange = async (newStatus: string) => {
    if (!req || newStatus === req.status) return;
    setUpdatingStatus(true);
    try {
      const res = await api.put<{ success: boolean; requisition: Requisition }>(`/api/requisitions/${req.id}`, { status: newStatus });
      if (res.success) {
        setReq(res.requisition);
        toast.success(`Status updated to ${formatStatusLabel(newStatus)}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading requisition...</p>
      </div>
    );
  }

  const isViewer = req?.requisitionViewers?.some((rv) => String(rv.user.id) === String(currentUser?.id));
  const isAuthorized = canReadAll || (canReadOwn && String(req?.createdBy?.id) === String(currentUser?.id)) || isViewer;
  if (!isAuthorized) {
    return <AccessRestricted />;
  }

  if (!req) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <p className="text-sm font-medium text-gray-500">Requisition not found.</p>
        <Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate("/recruitment")}>
          Back to Requisitions
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate("/recruitment")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Requisitions</span>
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Briefcase size={28} className="text-gray-900" />
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              {req.jobRole?.roleName ?? "Unnamed Position"}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusBadgeStyle(req.status)}`}
            >
              {formatStatusLabel(req.status)}
            </Badge>
            <Badge
              variant="outline"
              className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getUrgencyBadgeStyle(req.urgency)}`}
            >
              {req.urgency}
            </Badge>
            <span className="text-sm text-gray-500 font-medium">
              {req.department?.departmentName ?? "—"} · {req.jobPositions.length} position
              {req.jobPositions.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {!isViewer && (
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-10 rounded-xl text-sm font-bold"
              onClick={() => navigate(`/recruitment/requisitions/${req.id}/edit`)}
            >
              <Pencil size={16} />
              <span>Edit</span>
            </Button>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold"
              onClick={() => navigate(`/recruitment/requisitions/${req.id}/add-candidate`)}
            >
              <UserPlus2 size={16} />
              <span>Add Candidate</span>
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative"
          >
            <div className="flex justify-between items-start">
              <div className="z-10">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                  {stat.label}
                </p>
                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                  {stat.value}
                </h3>
              </div>
              <div
                className={`p-2.5 rounded-xl transition-colors z-10 ${stat.iconBg} group-hover:${stat.iconBg}`}
              >
                <div className={`${stat.iconColor} transition-colors`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-2">
          <TabsTrigger
            value="overview"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="candidates"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Candidates ({totalCandidates})
          </TabsTrigger>
          <TabsTrigger
            value="job-details"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Job Details
          </TabsTrigger>
          <TabsTrigger
            value="viewers"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Viewers
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Requisition Summary */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                <h4 className="font-bold text-gray-900 text-base">
                  Requisition Summary
                </h4>

                {/* Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Job Role
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">
                      {req.jobRole?.roleName ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Department
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">
                      {req.department?.departmentName ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Created By
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">
                      {req.createdBy?.name ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Reports To
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">
                      {req.reportsTo?.name ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Requisition Date
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">
                      {formatDate(req.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Required Start Date
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">
                      {formatDate(req.startDate)}
                    </p>
                  </div>
                </div>

                {/* Job Positions */}
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1.5">
                    Job Position{req.jobPositions.length !== 1 ? "s" : ""}
                  </p>
                  <div className="space-y-2">
                    {req.jobPositions.map((pos) => (
                      <div key={pos.id} className="p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                        <p className="text-sm font-bold text-gray-900">
                          {pos.positionCode}
                        </p>
                        {pos.location && (
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            {pos.location}
                          </p>
                        )}
                      </div>
                    ))}
                    {req.jobPositions.length === 0 && (
                      <p className="text-sm text-gray-500 font-medium">No positions linked</p>
                    )}
                  </div>
                </div>

                {/* Justification */}
                {req.businessJustification && (
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1.5">
                      Justification
                    </p>
                    <p className="text-sm font-medium text-gray-700">
                      {req.businessJustification}
                    </p>
                  </div>
                )}

                {/* Job Grade */}
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1.5">
                    Job Grade
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {req.jobRole?.jobGrade?.name ?? "—"}
                  </p>
                </div>

                {/* Grading Scale */}
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1.5">
                    Grading Scale
                  </p>
                  {req.jobRole?.jobGrade ? (
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Minimum</p>
                        <p className="text-sm font-medium text-gray-700">
                          {req.jobRole.jobGrade.salaryRangeMin !== undefined && req.jobRole.jobGrade.salaryRangeMin !== null
                            ? `$${req.jobRole.jobGrade.salaryRangeMin.toLocaleString()}`
                            : "Not specified"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Maximum</p>
                        <p className="text-sm font-medium text-gray-700">
                          {req.jobRole.jobGrade.salaryRangeMax !== undefined && req.jobRole.jobGrade.salaryRangeMax !== null
                            ? `$${req.jobRole.jobGrade.salaryRangeMax.toLocaleString()}`
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-bold text-gray-900">
                      {formatSalary(req.salaryRangeMin, req.salaryRangeMax)}
                    </p>
                  )}
                </div>

              </div>

              {/* Recent Candidates */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 text-base">
                    Recent Candidates
                  </h4>
                  {candidates.length > 0 && (
                    <Button
                      variant="outline"
                      className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-9 rounded-xl text-xs font-bold px-3"
                      onClick={() => setActiveTab("candidates")}
                    >
                      View All
                    </Button>
                  )}
                </div>

                {candidates.length === 0 ? (
                  <p className="text-sm text-gray-500 font-medium py-4">No candidates yet.</p>
                ) : (
                  <div className="space-y-3">
                    {candidates.slice(0, 3).map((candidate) => (
                      <div
                        key={candidate.id}
                        className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <div>
                          <p className="text-sm font-bold text-gray-900">
                            {candidate.firstName} {candidate.lastName}
                          </p>
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            {candidate.currentPosition ?? "—"} · Applied {formatShortDate(candidate.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <Badge
                            variant="outline"
                            className={`rounded-full border-0 font-bold px-2.5 py-1 text-[10px] ${getCandidateStatusStyle(candidate.status)}`}
                          >
                            {formatCandidateStatus(candidate.status)}
                          </Badge>
                          <button
                            onClick={() =>
                              navigate(`/recruitment/candidates/${candidate.id}`)
                            }
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-1 space-y-6">
              {/* Status Management */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <Zap size={18} className="text-blue-500" />
                  <h4 className="font-bold text-gray-900 text-base">
                    Status Management
                  </h4>
                </div>
                <p className="text-gray-500 text-sm font-medium">
                  Update status following workflow rules
                </p>

                <div className="p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                  <Badge
                    variant="outline"
                    className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusBadgeStyle(req.status)}`}
                  >
                    {formatStatusLabel(req.status)}
                  </Badge>
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                    Current Status:
                  </p>
                  <p className="text-sm font-medium text-gray-600">
                    {req.status === "OPEN" && "Requisition is open and accepting candidates"}
                    {req.status === "IN_PROGRESS" && "Recruitment is actively in progress"}
                    {req.status === "ON_HOLD" && "Recruitment is temporarily on hold"}
                    {req.status === "COMPLETE" && "Recruitment has been completed"}
                    {req.status === "CANCELLED" && "Requisition has been cancelled"}
                  </p>
                </div>

                {!isViewer && allowedNextStatuses.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                      Change Status:
                    </p>
                    <Select value={req.status} onValueChange={handleStatusChange} disabled={updatingStatus}>
                      <SelectTrigger className="bg-white border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value={req.status}>
                          {formatStatusLabel(req.status)} (Current)
                        </SelectItem>
                        {allowedNextStatuses.map((s) => (
                          <SelectItem key={s} value={s}>
                            {formatStatusLabel(s)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {!isViewer && (
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-3">
                  <h4 className="font-bold text-gray-900 text-base">
                    Quick Actions
                  </h4>

                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left"
                    onClick={() => navigate(`/recruitment/requisitions/${req.id}/add-candidate`)}
                  >
                    <UserPlus2 size={18} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-900">
                      Add Candidate
                    </span>
                  </button>
                  <button className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left">
                    <Calendar size={18} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-900">
                      Schedule Interview
                    </span>
                  </button>
                  <button className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left">
                    <FileText size={18} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-900">
                      Export Report
                    </span>
                  </button>
                </div>
              )}

            </div>
          </div>
        </TabsContent>

        <TabsContent value="candidates" className="mt-0 space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            {/* Search & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="relative w-full sm:max-w-sm">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search candidates..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all"
                />
              </div>
              {!isViewer && (
                <Button
                  className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                  onClick={() => navigate(`/recruitment/requisitions/${req.id}/add-candidate`)}
                >
                  <UserPlus2 size={16} />
                  <span>Add Candidate</span>
                </Button>
              )}
            </div>

            {candidates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm font-medium text-gray-500">No candidates yet. Add your first candidate to get started.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto no-scrollbar">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50/50">
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                          Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Current Position
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Experience
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Source
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Applied
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {candidates.map((candidate) => (
                        <tr key={candidate.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-5">
                            <div>
                              <p className="text-sm font-bold text-gray-900">
                                {candidate.firstName} {candidate.lastName}
                              </p>
                              <p className="text-xs text-gray-500 font-medium mt-0.5">
                                {candidate.email}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div>
                              <p className="text-sm font-bold text-gray-900">
                                {candidate.currentPosition ?? "—"}
                              </p>
                              <p className="text-xs text-gray-500 font-medium mt-0.5">
                                {candidate.currentCompany ?? "—"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-sm font-bold text-gray-600">
                              {candidate.yearsExperience ? `${candidate.yearsExperience} years` : "—"}
                            </span>
                          </td>
                          <td className="px-6 py-5">
                            <Badge
                              variant="outline"
                              className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getCandidateStatusStyle(candidate.status)}`}
                            >
                              {formatCandidateStatus(candidate.status)}
                            </Badge>
                          </td>
                          <td className="px-6 py-5">
                            <Badge
                              variant="outline"
                              className="rounded-full border border-gray-200 font-bold px-2.5 py-1 text-[10px] bg-white text-gray-700"
                            >
                              {candidate.source ?? "—"}
                            </Badge>
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-sm font-bold text-gray-600">
                              {formatShortDate(candidate.createdAt)}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => navigate(`/recruitment/candidates/${candidate.id}`)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                              >
                                <Eye size={16} />
                              </button>
                              <button className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                                <MessageSquare size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {candidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 text-sm">
                            {candidate.firstName} {candidate.lastName}
                          </p>
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            {candidate.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => navigate(`/recruitment/candidates/${candidate.id}`)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          >
                            <Eye size={16} />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                            <MessageSquare size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                            Position
                          </span>
                          <span className="font-bold text-gray-700 block">
                            {candidate.currentPosition ?? "—"}
                          </span>
                          <span className="text-gray-500 font-medium">
                            {candidate.currentCompany ?? "—"}
                          </span>
                        </div>
                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                            Experience
                          </span>
                          <span className="font-bold text-gray-700">
                            {candidate.yearsExperience ? `${candidate.yearsExperience} years` : "—"}
                          </span>
                        </div>
                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                            Status
                          </span>
                          <Badge
                            variant="outline"
                            className={`rounded-lg border-0 font-bold px-2 py-0.5 text-[10px] ${getCandidateStatusStyle(candidate.status)}`}
                          >
                            {formatCandidateStatus(candidate.status)}
                          </Badge>
                        </div>
                        <div className="bg-white rounded-xl px-3 py-2 border border-gray-100">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">
                            Source
                          </span>
                          <Badge
                            variant="outline"
                            className="rounded-full border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-700"
                          >
                            {candidate.source ?? "—"}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">
                          Applied
                        </span>
                        <span className="font-bold text-gray-700 ml-2">
                          {formatShortDate(candidate.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="job-details" className="mt-0 space-y-6">
          {/* Qualifications Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Required Certificates & Licenses */}
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <h4 className="font-bold text-gray-900 text-base">
                Required Certificates, Licenses & Qualifications
              </h4>
              {req.keyDuties && req.keyDuties.length > 0 ? (
                <ul className="space-y-3">
                  {req.keyDuties.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 font-medium">No required qualifications specified.</p>
              )}
            </div>

            {/* Preferred Qualifications */}
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <h4 className="font-bold text-gray-900 text-base">
                Preferred Qualifications
              </h4>
              {req.outcomes && req.outcomes.length > 0 ? (
                <ul className="space-y-3">
                  {req.outcomes.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <Star size={16} className="text-amber-400 shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 font-medium">No preferred qualifications specified.</p>
              )}
            </div>
          </div>

          {/* Key Responsibilities / Key Duties */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">
              Key Responsibilities
            </h4>
            {req.keyDuties && req.keyDuties.length > 0 ? (
              <ul className="space-y-3">
                {req.keyDuties.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 mt-2" />
                    <span className="text-sm font-medium text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 font-medium">No key responsibilities specified.</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="viewers" className="mt-0 space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Requisition Viewers</h4>
              <p className="text-gray-500 text-sm font-medium">
                Users who have been invited to view this requisition
              </p>
            </div>

            {/* Add Viewer — searchable combobox */}
            {canManageViewers && (
              <div className="flex items-center gap-3">
                <Popover open={viewerPopoverOpen} onOpenChange={setViewerPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={viewerPopoverOpen}
                      className="w-full sm:w-[320px] justify-between bg-gray-50/50 border-gray-100 hover:bg-white rounded-xl h-11 text-sm font-medium"
                    >
                      <span className="text-gray-500">Search and select a viewer...</span>
                      <Search size={16} className="text-gray-400 shrink-0 ml-2" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full sm:w-[320px] p-0 rounded-xl border-gray-200">
                    <Command>
                      <CommandInput placeholder="Search users..." className="h-10 text-sm" />
                      <CommandList>
                        <CommandEmpty>No users found.</CommandEmpty>
                        <CommandGroup>
                          {availableViewers
                            .filter((u) => !req.requisitionViewers?.some((v) => v.user.id === u.id))
                            .map((u) => (
                              <CommandItem
                                key={u.id}
                                value={u.name}
                                onSelect={() => {
                                  setViewerPopoverOpen(false);
                                  api.post<{ success: boolean; viewer: { id: number; user: { id: number; name: string; email: string } } }>(`/api/requisitions/${req.id}/viewers`, {
                                    userId: u.id,
                                  }).then((res) => {
                                    if (res.success) {
                                      setReq((prev) => {
                                        if (!prev) return prev;
                                        return { ...prev, requisitionViewers: [...prev.requisitionViewers, res.viewer] };
                                      });
                                      toast.success("Viewer added");
                                    }
                                  }).catch((err: any) => {
                                    toast.error(err.message || "Failed to add viewer");
                                  });
                                }}
                                className="text-sm font-medium cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-bold text-gray-900">{u.name}</span>
                                  <span className="text-xs text-gray-500">{u.email}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Viewers List */}
            {req.requisitionViewers?.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 font-medium">No viewers have been invited to this requisition yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {req.requisitionViewers?.map((viewer) => (
                  <div
                    key={viewer.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-sm font-bold">
                        {viewer.user.name?.split(" ").map((n) => n[0]).join("")}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{viewer.user.name}</p>
                        <p className="text-xs text-gray-500 font-medium">{viewer.user.email}</p>
                      </div>
                    </div>
                    {canManageViewers && (
                      <button
                        onClick={() => handleRemoveViewer(viewer.user.id)}
                        disabled={removingViewerId === viewer.user.id}
                        className="text-gray-400 hover:text-red-500 transition-colors p-2 disabled:opacity-50"
                        title="Remove viewer"
                      >
                        {removingViewerId === viewer.user.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Activity Log</h4>
              <p className="text-gray-500 text-sm font-medium">
                Recent changes and updates to this recruitment request
              </p>
            </div>

            <div className="space-y-0">
              {[
                {
                  action: "Recruitment request created",
                  user: req.createdBy?.name ?? "—",
                  date: formatDate(req.createdAt),
                },
                ...(req.startDate ? [{
                  action: "Required start date set",
                  user: req.createdBy?.name ?? "—",
                  date: formatDate(req.startDate),
                }] : []),
              ].map((activity, index, arr) => (
                <div key={index}>
                  <div className="flex items-start gap-4 py-4">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <Clock size={14} className="text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {activity.action}
                      </p>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {activity.user} · {activity.date}
                      </p>
                    </div>
                  </div>
                  {index < arr.length - 1 && (
                    <div className="ml-4 pl-4 border-t border-gray-100" />
                  )}
                </div>
              ))}
              {req.createdAt === req.createdAt && (
                <div>
                  <div className="flex items-start gap-4 py-4">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                      <CheckCircle size={14} className="text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        Current status: {formatStatusLabel(req.status)}
                      </p>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {req.department?.departmentName ?? "—"} · {req.jobPositions.length} position{req.jobPositions.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Candidate removed — now a separate page */}
    </div>
  );
};

export default RequisitionDetail;
